import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { ApolloError } from '@apollo/client/errors'
import {
  useFindInvoiceLazyQuery,
  useFindPartnerByPaymentsPortalUrlLazyQuery,
  CustomerPartnerFragment,
  CustomerFindInvoiceFragment,
  CustomerCreditCardFeeFragment,
  CustomerFeeSplitResultFragment,
  useCustomerCalculatePaymentFeeLazyQuery,
  MethodEnum
} from 'graph/generated/payments/graphql-types'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { createBrandColor } from 'utils/colors'
import { AppCookie } from 'utils/cookies'
import { PaymentMethod } from 'utils/payments'
import { actionTracking } from 'utils/posthog'
import { ColorProps } from 'utils/types'
import { origin, getPaymentsDomain } from 'utils/window'

import useCustomInvoiceData from './useCustomInvoiceData'
import usePaymentRequestInvoiceData from './usePaymentRequestInvoiceData'

/**
 * Provider that supplies company information when a company url is provided.
 * Supplies invoice information when id is url param
 */

interface ICompanyAndInvoiceContext {
  company: CustomerPartnerFragment
  brandColor: ColorProps
  invoice: CustomerFindInvoiceFragment | null
  /** The `[invoice]` route param; null off invoice routes. */
  routeInvoiceId: string | null
  error: ApolloError
  loading: boolean
  paymentAmount: number
  setPaymentAmount: (amount: number) => void
  installmentTerm: number
  setInstallmentTerm: (term: number) => void
  payInInstallments: boolean
  setPayInInstallments: (payInInstallments: boolean) => void
  payAsGuest: boolean
  setPayAsGuest: (payAsGuest: boolean) => void
  cardFee: CustomerCreditCardFeeFragment | null
  setCardFee: (creditCardFee: CustomerCreditCardFeeFragment) => void
  isCustomInvoice: boolean
  methodSelected: PaymentMethod | null
  setMethodSelected: (method: PaymentMethod) => void
  isEditingPayAmount: boolean
  setIsEditingPayAmount: (value: boolean) => void
  isEditingPaymentMethod: boolean
  setIsEditingPaymentMethod: (value: boolean) => void
  isPaymentRequest: boolean
  paymentRequestID: string
  paymentFee: CustomerFeeSplitResultFragment | null
  setPaymentFee: (fee: CustomerFeeSplitResultFragment) => void
}

export const CompanyAndInvoiceContext =
  createContext<ICompanyAndInvoiceContext>({
    company: null,
    brandColor: null,
    invoice: null,
    routeInvoiceId: null,
    error: null,
    loading: false,
    paymentAmount: 0,
    setPaymentAmount: () => {},
    installmentTerm: 1,
    setInstallmentTerm: () => {},
    payInInstallments: false,
    setPayInInstallments: () => {},
    payAsGuest: false,
    setPayAsGuest: () => {},
    cardFee: null,
    setCardFee: () => {},
    isCustomInvoice: false,
    methodSelected: null,
    setMethodSelected: () => {},
    isEditingPayAmount: false,
    setIsEditingPayAmount: () => {},
    isEditingPaymentMethod: false,
    setIsEditingPaymentMethod: () => {},
    isPaymentRequest: false,
    paymentRequestID: '',
    paymentFee: null,
    setPaymentFee: () => {}
  })

const CompanyAndInvoiceProvider = ({ children }) => {
  const {
    query: { invoice },
    pathname
  } = useRouter()
  const isCustomInvoice = pathname.includes('/custom-invoice')

  const customInvoiceData = useCustomInvoiceData(pathname)
  const {
    invoice: paymentRequestInvoiceData,
    isPaymentRequest,
    paymentRequestID
  } = usePaymentRequestInvoiceData()

  const [installmentTerm, setInstallmentTerm] = useState<number>(1)
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null)
  const [payInInstallments, setPayInInstallments] = useState<boolean>(false)
  const [payAsGuest, setPayAsGuest] = useState<boolean>(
    Cookies.get(AppCookie.LoggedAsGuest) === 'true'
  )
  const [cardFee, setCardFee] = useState<CustomerCreditCardFeeFragment>(null)
  const [methodSelected, setMethodSelected] = useState<PaymentMethod>()
  const [isEditingPayAmount, setIsEditingPayAmount] = useState<boolean>(false)
  const [isEditingPaymentMethod, setIsEditingPaymentMethod] =
    useState<boolean>(false)
  const [paymentFee, setPaymentFee] =
    useState<CustomerFeeSplitResultFragment>(null)

  const [fetchCompany, companyQuery] =
    useFindPartnerByPaymentsPortalUrlLazyQuery()
  const [fetchInvoice, invoiceQuery] = useFindInvoiceLazyQuery()

  // Store company data to prevent it from being overwritten by invoice query
  const [company, setCompany] = useState(null)

  const value = useMemo(() => {
    const invoiceData = isCustomInvoice
      ? customInvoiceData
      : isPaymentRequest
      ? paymentRequestInvoiceData
      : invoiceQuery.data?.FindInvoice

    if (!paymentAmount) {
      setPaymentAmount(invoiceData?.outstandingAmount ?? 0)
    }

    return {
      company,
      brandColor: createBrandColor(company?.paymentsPortalCustomization?.color),
      invoice: invoiceData,
      // `query` also carries search params (e.g. /dashboard/history?invoice=…)
      routeInvoiceId: pathname.startsWith('/[invoice]')
        ? (invoice as string) ?? null
        : null,
      loading: companyQuery.loading,
      error: companyQuery.error || invoiceQuery.error,
      paymentAmount,
      setPaymentAmount,
      installmentTerm,
      setInstallmentTerm,
      payInInstallments,
      setPayInInstallments,
      payAsGuest,
      setPayAsGuest,
      cardFee,
      setCardFee,
      isCustomInvoice,
      methodSelected,
      setMethodSelected,
      isEditingPayAmount,
      setIsEditingPayAmount,
      isEditingPaymentMethod,
      setIsEditingPaymentMethod,
      isPaymentRequest,
      paymentRequestID,
      paymentFee,
      setPaymentFee
    }
  }, [
    company,
    invoice,
    pathname,
    invoiceQuery.data,
    companyQuery.loading,
    paymentAmount,
    installmentTerm,
    payInInstallments,
    payAsGuest,
    cardFee,
    isCustomInvoice,
    methodSelected,
    isEditingPayAmount,
    isEditingPaymentMethod,
    isPaymentRequest,
    paymentRequestID,
    paymentRequestInvoiceData,
    paymentFee
  ])

  // Reset state when invoice changes or on initial load
  useEffect(() => {
    setIsEditingPayAmount(false)
  }, [invoice])

  useEffect(() => {
    if (pathname.includes('installments')) {
      setPayInInstallments(true)
    }
  }, [pathname])

  useEffect(() => {
    const currentDomain = getPaymentsDomain()
    if (currentDomain) {
      actionTracking({
        action: 'calling FindPartnerByPaymentsPortalUrlQuery',
        properties: {
          url: currentDomain,
          origin: origin,
          location: window.location
        }
      })
      fetchCompany({
        variables: { url: currentDomain }
      }).then(result => {
        const company = result.data?.FindPartnerByPaymentsPortalUrl
        if (company) {
          setCompany(company)
        }
      })
    }
  }, [fetchCompany, setCompany])

  useEffect(() => {
    if (invoice) {
      setPaymentAmount(null)
      setInstallmentTerm(1)
      if (!isCustomInvoice) {
        fetchInvoice({ variables: { id: invoice as string } })
      }
    }
  }, [invoice, fetchInvoice, isCustomInvoice])

  // Calculate payment fee
  const [calculatePaymentFee] = useCustomerCalculatePaymentFeeLazyQuery()
  const fetchAndSetPaymentFee = async () => {
    setPaymentFee(null)

    if (
      methodSelected?.__typename != 'Card' ||
      !methodSelected?.id ||
      !value?.company?.id ||
      !value?.invoice?.customer?.id ||
      !value?.paymentAmount
    ) {
      return
    }

    const fee = await calculatePaymentFee({
      variables: {
        input: {
          partnerID: value.company.id,
          customerID: value.invoice.customer.id,
          paymentMethodID: value.methodSelected.id,
          paymentMethodType: MethodEnum.Card,
          amount: value.paymentAmount,
          currency: value.company.currency
        }
      },
      fetchPolicy: 'cache-and-network'
    })
    setPaymentFee(fee?.data?.CustomerCalculatePaymentFee)
  }
  useEffect(() => {
    fetchAndSetPaymentFee()
  }, [value?.paymentAmount, value?.methodSelected])

  return (
    <CompanyAndInvoiceContext.Provider value={value}>
      {children}
    </CompanyAndInvoiceContext.Provider>
  )
}

export const useCompanyAndInvoiceContext = () =>
  useContext(CompanyAndInvoiceContext)

export default CompanyAndInvoiceProvider
